"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  ArrowRight,
  Mic,
  Crown,
  Compass,
  Target,
  Eye,
  Activity,
  BarChart,
  Layers,
  Network,
  Lightbulb,
  CheckCircle,
  Shield,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { BentoBlock } from "@/lib/blocks/bento-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

export default function SovereignOperationsArchitecturePage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary · Sovereign Operations Architecture"
        overlineClassName="text-emerald"
        title={
          <>
            The Six-Stage Methodology That <span className="text-gradient-premium">Commands Sovereign Outcomes</span>
          </>
        }
        subtitle="The six-stage engagement methodology that governs every sovereign-scale engagement — Intelligence, Analysis, Strategy, Orchestration, Amplification, Feedback. Each stage activates specific platforms within the Expanded Penta P's Framework."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary", href: "/glossary/" }, { label: "Sovereign Operations Architecture" }]}
        badge="Six stages · Battle-tested"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-5`}>
            <Sparkles className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Formal Definition</span>
          </div>
          <h2 className="text-display-md mb-5">What Sovereign Operations Architecture is.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            <span className="font-semibold text-foreground">Sovereign Operations Architecture</span> is the six-stage engagement methodology that governs every sovereign-scale engagement — from national-level political campaigns and sovereign digital transformation projects to Fortune 100 crisis recoveries. It is not a consulting framework. It is a battle-tested operational methodology designed for outcomes that conventional project management cannot achieve.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The architecture operates through six sequential stages — Intelligence, Analysis, Strategy, Orchestration, Amplification, and Feedback — each activating specific platforms within the Expanded Penta P's Framework. Every stage produces deliverables that feed the next, and the Feedback stage closes the loop back into Intelligence, creating a continuous learning cycle that compounds across engagements.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The term "sovereign" reflects the scale and stakes: this architecture was built for engagements where the outcome determines the trajectory of nations, institutions, and leaders operating at the highest levels of global influence. It is the operational backbone that transforms nine proprietary AI platforms from a collection of tools into an integrated ecosystem.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Six Stages</span>
          </div>
          <h2 className="text-display-md mb-4">The sequential operational logic.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each stage activates specific platforms, deploys specific teams, and produces specific deliverables. The architecture ensures that no stage operates in isolation.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { num: "01", title: "Intelligence — We Discover Before We Decide", body: "Every engagement begins with intelligence gathering. Digital landscape interrogation across 200+ platforms. Ground truth collection through field operatives. Competitive environment mapping. Output: comprehensive intelligence baseline with quantified confidence scores.", icon: Eye, accent: "red" },
            { num: "02", title: "Analysis — We Find What Others Miss", body: "Intelligence without analysis is noise. The Analysis stage transforms raw data into structured understanding through pattern identification, vulnerability assessment, and audience segmentation. Output: structured analytical frameworks, prioritized threat matrices, segmented audience profiles.", icon: BarChart, accent: "purple" },
            { num: "03", title: "Strategy — We Architect Victory", body: "With intelligence as foundation and analysis as lens, the Strategy stage deploys the full Expanded Penta P's framework. Pillar assignment, platform activation planning, resource architecture. Output: an operational plan that drives coordinated action across all nine platforms simultaneously.", icon: Lightbulb, accent: "cyan" },
            { num: "04", title: "Orchestration — We Coordinate Execution", body: "Multiple platforms, multiple teams, multiple geographies — orchestrated in real time. The neural command interface synchronizes operations across countries and platforms. Output: coordinated multi-platform execution at sub-100 millisecond coordination speeds.", icon: Compass, accent: "emerald" },
            { num: "05", title: "Amplification — We Maximize Impact", body: "Intelligence becomes impact through the distribution and amplification stage. Content deploys through multi-platform channels. Reach and engagement are measured against baselines. Output: 340% reach increase. 250% engagement improvement. Compound visibility.", icon: Activity, accent: "amber-gold" },
            { num: "06", title: "Feedback — We Compound Intelligence", body: "The loop never closes — it accelerates. Outcome data flows back into the intelligence stage. Early warning systems detect trends before they become crises. Every cycle is smarter than the last. Output: compounding institutional knowledge, refined methodologies, verified outcomes.", icon: CheckCircle, accent: "denim" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-lg shrink-0`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-overline ${a.text}`}>Stage {p.num}</span>
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Target className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Why the Architecture Exists</span>
          </div>
          <h2 className="text-display-md mb-4">Conventional frameworks fail at sovereign scale.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Conventional consulting frameworks were designed for predictable environments. The Sovereign Operations Architecture was designed for the opposite.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { dim: "Environments", body: "Where the stakes are existential, the timeline is compressed, and the variables are multiplying faster than human analysis can process them.", accent: "red" },
            { dim: "Integration", body: "When a crisis strikes, three pillars must activate simultaneously. When a national election campaign launches, all five pillars must operate in unison.", accent: "purple" },
            { dim: "Speed", body: "384x to 1,416x faster than traditional approaches. 95% coordination success rate. Sub-100 millisecond cross-platform coordination.", accent: "cyan" },
            { dim: "Outcomes", body: "The architecture exists because the challenges facing governments, royal families, defense agencies, and Fortune-scale corporations demand an integrated approach.", accent: "emerald" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.dim}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-sm mb-2 ${a.text}`}>{p.dim}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Network className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Related Terms</span>
          </div>
          <h2 className="text-display-md mb-4">The conceptual map.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { term: "Penta P's Framework", href: "/glossary/penta-ps-framework/", desc: "The five-pillar architecture the methodology operationalizes.", accent: "cyan" },
            { term: "Strategic Sovereignty", href: "/glossary/strategic-sovereignty/", desc: "The integrated discipline the architecture produces.", accent: "red" },
            { term: "Operational Excellence", href: "/glossary/operational-excellence/", desc: "The engineered system the architecture builds upon.", accent: "amber-gold" },
            { term: "Digital Conglomerate", href: "/glossary/digital-conglomerate/", desc: "The organizational form that executes the architecture.", accent: "purple" },
            { term: "Crisis Transformation", href: "/glossary/crisis-transformation/", desc: "The perception-pillar discipline for converting emergencies to advantage.", accent: "coral" },
            { term: "Perception Management", href: "/glossary/perception-management/", desc: "The Perception pillar in operational detail.", accent: "red" },
          ].map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.term}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.desc}</p>
                    <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read <ArrowRight className="size-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Architecture Layers</span>
          </div>
          <h2 className="text-display-md mb-4">The architecture layer by layer.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Eight structural layers compose the Sovereign Operations Architecture. The bento layout reflects how the layers interlock — the large tiles carry the most operational mass, the smaller tiles complete the architecture.
          </p>
        </div>
        <BentoBlock
          items={[
            { title: "Intelligence Layer", body: "The first stage. OMNI-Surveillance, Predictive Intelligence Engine, Threat Detection Matrix. 89% prediction accuracy at 24-72 hour lead times.", icon: <Eye className="size-5" />, span: "md", accent: "red" },
            { title: "Analysis Layer", body: "Pattern identification, vulnerability assessment, audience segmentation. Raw data transformed into decision-ready intelligence.", icon: <BarChart className="size-5" />, span: "sm", accent: "purple" },
            { title: "Strategy Layer", body: "Pillar assignment, platform activation planning, resource architecture. The operational plan that drives coordinated action.", icon: <Lightbulb className="size-5" />, span: "sm", accent: "cyan" },
            { title: "Orchestration Layer", body: "Multiple platforms, multiple teams, multiple geographies — orchestrated in real time. Sub-100 millisecond cross-platform coordination.", icon: <Compass className="size-5" />, span: "lg", accent: "emerald" },
            { title: "Amplification Layer", body: "Multi-platform content distribution, reach optimization, engagement amplification. 340% reach increase, 250% engagement improvement.", icon: <Activity className="size-5" />, span: "sm", accent: "amber-gold" },
            { title: "Feedback Layer", body: "Outcome data flows back into the intelligence stage. Compounding institutional knowledge. Every cycle is smarter than the last.", icon: <CheckCircle className="size-5" />, span: "sm", accent: "denim" },
            { title: "Security Envelope", body: "Always-active across all six layers. Zero-trust architecture. 99.9999% uptime. Zero security incidents in 15+ years.", icon: <Shield className="size-5" />, span: "md", accent: "lime" },
            { title: "Command Coordination", body: "The neural command interface coordinates all six layers. 95% coordination success rate. 80% improvement in decision speed.", icon: <Target className="size-5" />, span: "sm", accent: "coral" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Compass className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Usage Examples</span>
          </div>
          <h2 className="text-display-md mb-4">The architecture in practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four examples of the Sovereign Operations Architecture in active engagement. Each example shows how the six sequential stages produce sovereign-grade outcomes.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "Multi-Country Election Cycle", body: "A national election cycle across eighteen countries. Intelligence gathered for 24 months. Strategy developed in stages. Orchestration coordinated in real time. Amplification across 50+ digital platforms. Feedback loops refined the methodology. Eighty-three point three percent success rate.", accent: "red" },
            { title: "Royal Family Engagement", body: "Multi-year engagement for a royal house spanning three continents. Intelligence, strategy, orchestration, amplification, and feedback applied across governance, perception, and security domains. Sustained operational excellence throughout the engagement.", accent: "purple" },
            { title: "Crisis Recovery for Fortune 500", body: "A Fortune 500 corporation under existential threat. Crisis response activates within 2 minutes. Assessment within 30 seconds. Response generation within 2 minutes. Full deployment within 5 minutes. Recovery exceeds the pre-crisis baseline within 90 days.", accent: "cyan" },
            { title: "Governance Transformation", body: "National governance transformation spanning welfare distribution, citizen service delivery, and institutional reform. The architecture supports the multi-year program through every stage, from intelligence gathering to feedback-driven refinement.", accent: "emerald" },
          ]}
          columns={2}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Common Misconceptions</span>
          </div>
          <h2 className="text-display-md mb-4">What the architecture is not.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six persistent misconceptions about the Sovereign Operations Architecture. The misconceptions are common because the architecture is often confused with conventional project management frameworks.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "Is the architecture the same as a project management methodology?", a: "No. Project management methodologies are designed for predictable environments with defined scope, timeline, and budget. The Sovereign Operations Architecture is designed for environments where the stakes are existential, the timeline is compressed, and the variables are multiplying faster than conventional analysis can process." },
            { q: "Are the six stages executed sequentially?", a: "No. The six stages operate in a continuous loop with overlapping execution. Intelligence flows continuously. Strategy adapts in real time. Orchestration is constant. The sequential naming reflects the operational logic, not the temporal sequence." },
            { q: "Can the architecture be applied to small engagements?", a: "Yes. The architecture is scalable. A focused advisory engagement and a multi-year sovereign transformation can both apply the six stages. The depth and duration vary. The framework does not." },
            { q: "Is the architecture a technology platform?", a: "No. The architecture is an operational methodology. The technology platforms are the substrate that enables the methodology. The architecture governs how the platforms are activated, coordinated, and refined." },
            { q: "Does the architecture require all six stages for every engagement?", a: "Yes. Every engagement begins with Intelligence and ends with Feedback. The depth of each stage varies based on the engagement's scope, complexity, and consequence. Skipping a stage produces a fragile outcome." },
            { q: "Is the architecture the same as the Penta P's Framework?", a: "No. The Penta P's Framework is the closed-loop system of five pillars (Perception, Privacy, Politics, Policing, Policy). The Sovereign Operations Architecture is the six-stage methodology that operationalizes the framework. The two are complementary, not equivalent." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Compass className="size-10 text-emerald mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the sovereign operations architecture.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The architecture is not a project. It is a sustained operational posture — the integration of intelligence, technology, strategy, and execution under a single command.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/glossary/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                Glossary Hub
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary Hub", description: "Ten terms defining sovereign-scale operations.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "FAQ: Operational Excellence", description: "The engineered system behind the architecture.", href: "/faq/operational-excellence/", icon: Target, accent: "amber-gold" },
          { title: "About Dr. Jyoti Kush", description: "The full story of the operational architect.", href: "/about/", icon: Crown, accent: "red" },
          { title: "The 70% Project", description: "The flagship initiative on women in leadership.", href: "/the-70-percent-project/", icon: Crown, accent: "emerald" },
        ]}
      />
    </>
  );
}
